// src/pages/settings/settings/ClinikoImportCard.tsx
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Modal, Progress, Space, Steps, Tag, Typography } from "antd";
import { CloudDownloadOutlined, ExperimentOutlined, RedoOutlined, WarningOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";

import { SectionCard, StatusTag } from "../../../components/shared";
import DetailDrawer from "../../../components/shared/DetailDrawer";

// API
import { getImportRun, listImportRuns, resumeImportRun, startImportRun } from "../../../api/healthcare/Cliniko/ClinikoImportApi";

// Types
import {
	CLINIKO_IMPORT_ACTIVE_STATUSES,
	CLINIKO_IMPORT_SECTIONS,
	ClinikoImportRun,
	ClinikoImportRunConflict,
	SectionResult,
} from "../../../types/healthcare/ClinikoImportTypes";
import { Integration } from "../../../types/admin/IntegrationTypes";

// Hooks
import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";

const { Text, Paragraph } = Typography;

/**
 * The poll cadence while a run is `pending` or `running`.
 *
 * ⚠️ **THE POLL IS `refetchInterval`, NOT A `setInterval` IN AN EFFECT — DELIBERATELY.** An import
 * runs for hours, so this card is exactly the kind a clinic leaves open on a spare monitor, and a
 * hand-rolled interval that outlives its run (or is re-created on every re-render) is the known
 * failure mode here. React Query owns the timer's whole lifecycle: returning `false` from this
 * callback stops it, and unmounting disposes it. Same shape as `ComprehensiveAnalysisRenderer`'s
 * analysis poll, which is the repo's precedent for exactly this.
 */
const POLL_INTERVAL_MS = 5000;

/** `pending` / `running` — the two statuses that keep the poll alive and block a second start. */
const isRunActive = (run?: ClinikoImportRun | null): boolean =>
	!!run && CLINIKO_IMPORT_ACTIVE_STATUSES.includes(run.status);

/** `partial` and `failed` are resumable; `completed` is refused by the server with a 409. */
const isResumable = (run?: ClinikoImportRun | null): boolean =>
	!!run && (run.status === "partial" || run.status === "failed");

/**
 * The run vocabulary, stated here rather than added to the shared `StatusTag` map.
 *
 * ⚠️ `running` is not in that map and would render as an unstyled grey tag reading the raw column
 * value. Adding a `running` row to a map a dozen other pages read is a wider change than this card
 * needs, so the label and colour are passed in — the same escape hatch `StatusTag` already exposes.
 */
const RUN_STATUS_TAG: Record<string, { label: string; color?: string }> = {
	pending: { label: "Queued" },
	running: { label: "Running", color: "var(--mg-primary)" },
	completed: { label: "Completed" },
	partial: { label: "Partial" },
	failed: { label: "Failed" },
};

/**
 * A section that did not run, and says so.
 *
 * ⚠️ **A SKIP IS RECORDED AS `success: true`** so it does not inflate `sections_failed` — which
 * means rendering it as a green tick is one `if` away, and would give exactly the wrong assurance
 * before a one-shot migration of a live clinic. Every skip is rendered as a neutral step with the
 * reason spelled out.
 */
const SKIP_REASONS: Record<string, string> = {
	skipped_dry_run_unsupported: "this section cannot be rehearsed, so the dry run declined it — it will run for real on a live import",
	skipped_not_implemented: "its importer has not shipped yet",
};

/**
 * Human labels for the warning CODES a section records.
 *
 * ⚠️ **`section_results` carries codes and counts, never clinical content**, so a warning arrives
 * as `code` or `code:detail` (`credit_notes_not_imported:12`). An unrecognised code is humanised
 * from the code itself rather than hidden — a section that ships after this card must still be
 * readable here, and a silently-dropped warning is worse than an ugly one.
 */
const WARNING_LABELS: Record<string, string> = {
	unmatched_practitioners: "Cliniko practitioners with no Xoe user — create their seats, then Resume BEFORE the appointments section runs",
	unmatched_practitioner: "Cliniko practitioner with no Xoe user — create the seat, then Resume BEFORE the appointments section runs",
	credit_notes_not_imported: "Credit notes were skipped and not imported",
	documents_skipped: "Documents skipped",
	skipped_documents: "Documents skipped",
	importer_warnings: "Warnings recorded in this importer's own report file",
	multi_business_account: "The Cliniko account has more than one business",
	no_taxes_configured: "No taxes are configured in Cliniko",
	pagination_guard_tripped: "A Cliniko fetch was truncated — every count in this run is a lower bound",
};

/** `credit_notes_not_imported` → `Credit notes not imported`. */
const humaniseCode = (code: string): string => {
	const spaced = code.replace(/[_-]+/g, " ").trim();
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const describeWarning = (warning: string): string => {
	const separator = warning.indexOf(":");
	const code = separator === -1 ? warning : warning.slice(0, separator);
	const detail = separator === -1 ? "" : warning.slice(separator + 1).trim();
	const label = WARNING_LABELS[code] ?? humaniseCode(code);

	return detail ? `${label} (${detail})` : label;
};

/** `sections_completed` counts skips, so the bar is the server's own number, stated as-is. */
const progressPercent = (run: ClinikoImportRun): number => {
	if (!run.sections_total) return 0;
	return Math.round((run.sections_completed / run.sections_total) * 100);
};

const sectionLabel = (key: string): string =>
	CLINIKO_IMPORT_SECTIONS.find((section) => section.key === key)?.label ?? humaniseCode(key);

/** The server sends the execution order; the local constant is the fallback and the label source. */
const sectionOrder = (run?: ClinikoImportRun | null): string[] =>
	run?.section_order?.length ? run.section_order : CLINIKO_IMPORT_SECTIONS.map((section) => section.key);

const stepStatus = (run: ClinikoImportRun, key: string, result?: SectionResult): "wait" | "process" | "finish" | "error" => {
	if (result) {
		if (!result.success) return "error";
		// A skip is NOT a finish — see SKIP_REASONS.
		return result.skipped ? "wait" : "finish";
	}
	if (run.current_section === key && isRunActive(run)) return "process";
	return "wait";
};

const formatTimestamp = (value: string | null): string => (value ? dayjs(value).format("D MMM YYYY, HH:mm") : "—");

/** Every warning on a run, grouped by section, in execution order. */
const warningsBySection = (run?: ClinikoImportRun | null): { key: string; label: string; result: SectionResult }[] => {
	if (!run?.section_results) return [];

	return sectionOrder(run)
		.map((key) => ({ key, label: sectionLabel(key), result: run.section_results?.[key] }))
		.filter((entry): entry is { key: string; label: string; result: SectionResult } => {
			if (!entry.result) return false;
			return (entry.result.warnings?.length ?? 0) > 0 || !!entry.result.error || !!entry.result.skipped;
		});
};

interface ClinikoImportCardProps {
	/** The clinic's Cliniko integration. The card is only rendered when one exists. */
	integration: Integration;
}

/**
 * ── The Cliniko → Xoe FULL IMPORT card (S14, ClickUp 86d418ur4) ───────────────────────────────
 *
 * ⚠️ **This is not the sync panel above it.** The sync routes mirror a Cliniko account the clinic
 * is still working in; this queues the ONE-SHOT migration that moves the clinic off it. The server
 * gates these routes on `hasCredentials()` ALONE, so a clinic that has already switched its
 * ongoing sync toggles off — which is what migrating means — can still import.
 *
 * ⚠️ **The warnings drawer is the operational point of this card, not decoration.** Practitioners
 * import SECOND of nine, and a practitioner with no Xoe user is reported as a warning rather than
 * created. Seeing that list while the run is still working through patients and clinical records
 * is what lets an operator create the seats and Resume before the appointments section attributes
 * a diary to nobody.
 */
const ClinikoImportCard: React.FC<ClinikoImportCardProps> = ({ integration }) => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotifications();

	const [startedUuid, setStartedUuid] = useState<string | null>(null);
	const [confirmLiveOpen, setConfirmLiveOpen] = useState<boolean>(false);
	const [warningsOpen, setWarningsOpen] = useState<boolean>(false);
	/** The server's own 409 text. Surfaced as a message, never as an error toast — see below. */
	const [conflictMessage, setConflictMessage] = useState<string | null>(null);

	const runsQuery = useQuery({
		queryKey: ["cliniko-import-runs"],
		queryFn: listImportRuns,
	});

	const latestRun = runsQuery.data?.data?.[0] ?? null;
	const selectedUuid = startedUuid ?? latestRun?.run_uuid ?? null;

	const runQuery = useQuery({
		queryKey: ["cliniko-import-run", selectedUuid],
		queryFn: () => getImportRun(selectedUuid as string),
		enabled: !!selectedUuid,
		// ⚠️ THE WHOLE POLL LIFECYCLE. `false` on a terminal status stops the timer; there is no
		// interval to clear on unmount because there is no interval of ours to leak.
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			return status && CLINIKO_IMPORT_ACTIVE_STATUSES.includes(status) ? POLL_INTERVAL_MS : false;
		},
	});

	// The list row and the detail row are the same shape (one server-side `format()`), so the list
	// renders the run immediately and the detail query refines it.
	//
	// ⚠️ The fallback is guarded on the uuid MATCHING. Right after a start, `startedUuid` is the new
	// run while the list still holds the previous one — falling back unguarded would show the old
	// run's terminal status for a beat and re-enable the start buttons over a run that has just
	// been queued.
	const run = runQuery.data ?? (latestRun && latestRun.run_uuid === selectedUuid ? latestRun : null);
	const active = isRunActive(run);

	/**
	 * ⚠️ **A 409 IS A REDIRECT, NOT AN ERROR.** The server refuses a second concurrent run and
	 * hands back the ACTIVE run's uuid. Toasting that as a failure would tell an operator their
	 * import broke when in fact it is already running — so it becomes a message, and the active
	 * uuid is adopted so the card starts watching the run they actually have.
	 */
	const handleMutationError = (error: unknown) => {
		if (axios.isAxiosError(error) && error.response?.status === 409) {
			const conflict = error.response.data as ClinikoImportRunConflict | undefined;
			setConflictMessage(conflict?.message ?? "A Cliniko import is already pending or running.");
			if (conflict?.run_uuid) {
				setStartedUuid(conflict.run_uuid);
			}
			queryClient.invalidateQueries({ queryKey: ["cliniko-import-runs"] });
			return;
		}

		showError(extractValidationErrors(error).join(" "));
	};

	const adoptRun = (runUuid: string) => {
		setConflictMessage(null);
		setStartedUuid(runUuid);
		queryClient.invalidateQueries({ queryKey: ["cliniko-import-runs"] });
		queryClient.invalidateQueries({ queryKey: ["cliniko-import-run", runUuid] });
	};

	const startMutation = useMutation({
		mutationFn: (dryRun: boolean) => startImportRun({ dry_run: dryRun }),
		onSuccess: (response) => {
			adoptRun(response.run_uuid);
			showSuccess(response.message || "Import queued.");
		},
		onError: handleMutationError,
	});

	const resumeMutation = useMutation({
		mutationFn: (runUuid: string) => resumeImportRun(runUuid),
		onSuccess: (response) => {
			adoptRun(response.run_uuid);
			showSuccess(response.message || "Resume queued. Completed sections will be skipped.");
		},
		onError: handleMutationError,
	});

	const busy = startMutation.isPending || resumeMutation.isPending;

	const handleStartLive = () => {
		setConfirmLiveOpen(false);
		startMutation.mutate(false);
	};

	const sections = sectionOrder(run);
	const warningSections = warningsBySection(run);
	const warningCount = warningSections.reduce((total, entry) => total + (entry.result.warnings?.length ?? 0), 0);

	/**
	 * The ONGOING sync's unmatched practitioners, from `sync_statistics`.
	 *
	 * Surfaced here because it is the same operator decision the import's practitioners section
	 * raises — create the missing seats — and because nothing on this page has ever rendered it:
	 * the statistics row reads a handful of hardcoded `.synced` scalars and this key has been
	 * arriving unread. It carries a NAME so an operator can recognise who to onboard.
	 */
	const unmatchedPractitioners = integration.sync_statistics?.last_sync_results?.practitioners?.unmatched_practitioners ?? [];

	// The badge counts everything the drawer will actually show, from BOTH sources. Counting only
	// the run's own warnings would render "Warnings (0)" on a button that opens onto a list of
	// practitioners who need seats — the one thing this drawer exists to make impossible to miss.
	const attentionCount = warningCount + unmatchedPractitioners.length;

	return (
		<SectionCard
			title="Cliniko full import"
			subtitle="One-shot migration of this clinic's Cliniko history into Xoe — separate from the ongoing sync above, and available even with the sync toggles off"
			style={{ marginTop: 16 }}
			extra={
				<Space>
					<Button
						icon={<ExperimentOutlined />}
						loading={startMutation.isPending && startMutation.variables === true}
						disabled={active || busy}
						onClick={() => startMutation.mutate(true)}
					>
						Start dry run
					</Button>
					<Button
						type="primary"
						icon={<CloudDownloadOutlined />}
						loading={startMutation.isPending && startMutation.variables === false}
						disabled={active || busy}
						onClick={() => setConfirmLiveOpen(true)}
					>
						Start import
					</Button>
				</Space>
			}
		>
			<Space direction="vertical" size="middle" style={{ width: "100%" }}>
				{active && (
					<Alert
						type="info"
						showIcon
						message="An import is already running"
						description="Only one run may be pending or running at a time — a second would double every Cliniko request against the same rate budget. The start buttons will re-enable when this run finishes."
					/>
				)}

				{conflictMessage && (
					<Alert type="warning" showIcon closable message={conflictMessage} onClose={() => setConflictMessage(null)} />
				)}

				{!run && !runsQuery.isLoading && (
					<Text type="secondary">No import has been run for this clinic yet. Start with a dry run.</Text>
				)}

				{run && (
					<>
						<Space size="middle" wrap>
							<StatusTag status={run.status} label={RUN_STATUS_TAG[run.status]?.label} color={RUN_STATUS_TAG[run.status]?.color} />
							<Tag color={run.mode === "dry_run" ? "blue" : "purple"}>{run.mode === "dry_run" ? "Dry run" : "Live import"}</Tag>
							<Text type="secondary">Run {run.run_uuid}</Text>
							<Text type="secondary">Started {formatTimestamp(run.started_at)}</Text>
							{run.completed_at && <Text type="secondary">Finished {formatTimestamp(run.completed_at)}</Text>}
						</Space>

						<div>
							<Progress
								percent={progressPercent(run)}
								status={run.status === "failed" ? "exception" : active ? "active" : "normal"}
							/>
							<Text type="secondary" style={{ fontSize: 12 }}>
								{run.sections_completed} of {run.sections_total} sections recorded
								{run.sections_failed > 0 ? `, ${run.sections_failed} failed` : ""}
								{run.current_section ? ` — currently importing ${sectionLabel(run.current_section)}` : ""}
							</Text>
						</div>

						{run.error_summary && <Alert type="error" showIcon message="Run error" description={run.error_summary} />}

						<Space wrap>
							<Button
								icon={<WarningOutlined />}
								onClick={() => setWarningsOpen(true)}
								disabled={attentionCount === 0 && warningSections.length === 0}
							>
								{`Warnings (${attentionCount})`}
							</Button>
							{isResumable(run) && (
								<Button
									type="primary"
									icon={<RedoOutlined />}
									loading={resumeMutation.isPending}
									disabled={busy}
									onClick={() => resumeMutation.mutate(run.run_uuid)}
								>
									Resume
								</Button>
							)}
						</Space>

						<Steps
							direction="vertical"
							size="small"
							items={sections.map((key) => {
								const result = run.section_results?.[key];
								const warnings = result?.warnings ?? [];

								return {
									title: sectionLabel(key),
									status: stepStatus(run, key, result),
									description: (
										<Space direction="vertical" size={0}>
											{result?.skipped && (
												<Text type="secondary">{`Skipped — ${SKIP_REASONS[result.skipped] ?? humaniseCode(result.skipped)}`}</Text>
											)}
											{result && !result.skipped && result.success && <Text type="secondary">{`${result.count} imported`}</Text>}
											{result && !result.success && (
												<Text type="danger">{`${result.error ?? "Failed"} (${result.count} imported before it stopped)`}</Text>
											)}
											{!result && run.current_section === key && active && <Text type="secondary">In progress…</Text>}
											{!result && run.current_section !== key && <Text type="secondary">Waiting</Text>}
											{warnings.length > 0 && (
												<Text type="warning">{`${warnings.length} warning${warnings.length === 1 ? "" : "s"}`}</Text>
											)}
										</Space>
									),
								};
							})}
						/>
					</>
				)}
			</Space>

			{/* Live-run confirmation — a controlled Modal rather than Modal.confirm, so the
			    confirmation is part of this component's own render tree. */}
			<Modal
				title="Start a live Cliniko import?"
				open={confirmLiveOpen}
				okText="Start live import"
				cancelText="Cancel"
				onOk={handleStartLive}
				onCancel={() => setConfirmLiveOpen(false)}
			>
				<Paragraph>
					This writes this clinic's Cliniko history into Xoe for real. It runs for hours, it cannot be
					undone from here, and only one run may be pending or running at a time.
				</Paragraph>
				<Paragraph>Run a dry run first if you have not already.</Paragraph>
			</Modal>

			<DetailDrawer title="Import warnings" open={warningsOpen} onClose={() => setWarningsOpen(false)} width={600}>
				<Space direction="vertical" size="middle" style={{ width: "100%" }}>
					{unmatchedPractitioners.length > 0 && (
						<Alert
							type="warning"
							showIcon
							message={`${unmatchedPractitioners.length} Cliniko practitioner${unmatchedPractitioners.length === 1 ? " has" : "s have"} no Xoe user`}
							description={
								<Space direction="vertical" size={4} style={{ width: "100%" }}>
									<Text>
										Create their seats now and Resume the run before the appointments section completes — clinical
										records and appointments imported after this point cannot be attributed to them.
									</Text>
									{unmatchedPractitioners.map((practitioner) => (
										<Text key={practitioner.cliniko_id} type="secondary">
											{`${practitioner.name} (Cliniko id ${practitioner.cliniko_id})`}
										</Text>
									))}
								</Space>
							}
						/>
					)}

					{warningSections.length === 0 && unmatchedPractitioners.length === 0 && (
						<Text type="secondary">No warnings were recorded for this run.</Text>
					)}

					{warningSections.map((entry) => (
						<div key={entry.key}>
							<Space size="small" style={{ marginBottom: 4 }}>
								<Text strong>{entry.label}</Text>
								{entry.result.skipped ? (
									<StatusTag status="waiting" label="Skipped" />
								) : (
									<StatusTag status={entry.result.success ? "completed" : "failed"} />
								)}
							</Space>

							<Space direction="vertical" size={2} style={{ width: "100%" }}>
								{entry.result.skipped && (
									<Text type="secondary">{`Skipped — ${SKIP_REASONS[entry.result.skipped] ?? humaniseCode(entry.result.skipped)}`}</Text>
								)}
								{entry.result.error && <Text type="danger">{entry.result.error}</Text>}
								{(entry.result.warnings ?? []).map((warning) => (
									<Text key={warning} type="warning">
										{describeWarning(warning)}
									</Text>
								))}
								{Object.entries(entry.result.counts ?? {}).map(([countKey, value]) => (
									<Text key={countKey} type="secondary" style={{ fontSize: 12 }}>
										{`${humaniseCode(countKey)}: ${String(value)}`}
									</Text>
								))}
							</Space>
						</div>
					))}
				</Space>
			</DetailDrawer>
		</SectionCard>
	);
};

export default ClinikoImportCard;
